/* UsageBadge — compact "47 / 1,000 sends" pill for the dashboard.
 *
 * Drives a soft visual warning as agencies approach their monthly limit:
 *   < 75% → emerald (healthy)
 *   75–94% → amber (heads-up)
 *   ≥ 95% → red (at-cap)
 *
 * Hidden entirely for unlimited tiers (send_limit = -1) so the pill
 * doesn't add noise on Compliance/Enterprise dashboards.
 */
import { Link } from 'react-router-dom';
import { Sparkles } from 'lucide-react';
import { usePlan } from '../hooks/usePlan';

const fmt = (n) => (typeof n === 'number' ? n.toLocaleString() : n);

export default function UsageBadge() {
  const { plan, usage, limits, loading } = usePlan();
  if (loading) return null;
  if (!plan || limits.send_limit === -1) return null;

  const pct = limits.send_limit > 0 ? (usage.sends / limits.send_limit) * 100 : 0;
  const tone =
    pct >= 95 ? { bar: 'bg-red-500',     pill: 'bg-red-50 text-red-700 border-red-200',         label: 'At cap' } :
    pct >= 75 ? { bar: 'bg-amber-500',   pill: 'bg-amber-50 text-amber-700 border-amber-200',   label: 'Heads up' } :
                { bar: 'bg-emerald-500', pill: 'bg-emerald-50 text-emerald-700 border-emerald-200', label: 'Healthy' };

  return (
    <div className="flex items-center gap-3" data-testid="usage-badge">
      <div className={`inline-flex items-center gap-2 px-3 py-1.5 rounded-full border text-xs font-medium ${tone.pill}`}>
        <span>{fmt(usage.sends)} / {fmt(limits.send_limit)} sends</span>
        <span className="opacity-50">·</span>
        <span className="capitalize">{plan.plan_name}</span>
        <div className="w-16 h-1.5 rounded-full bg-white/60 overflow-hidden">
          <div className={`h-full ${tone.bar}`} style={{ width: `${Math.min(100, pct)}%` }} />
        </div>
      </div>
      {pct >= 75 && (
        <Link
          to="/pricing"
          className="text-xs font-semibold text-slate-700 hover:text-slate-900 inline-flex items-center gap-1"
          data-testid="usage-badge-upgrade"
        >
          <Sparkles className="w-3 h-3" /> Upgrade
        </Link>
      )}
    </div>
  );
}
